import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { OrganizationConfig } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { PrintPageSize } from '../../../../../../models/organization-config-enums';
import { GiftPdfPreviewModalComponent } from './modals/gift-pdf-preview-modal/gift-pdf-preview-modal.component';
import { GiftPrintBannerImageOption } from '../../../../../../models/gift';
import { GiftService } from '../../../../../../services/gift/gift.service';
import {
  SimpleMessageModalComponent
} from '../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
import {
  ConfigureGiftPrintBannerImageOptionModalComponent
} from './modals/configure-gift-print-banner-image-option-modal/configure-gift-print-banner-image-option-modal.component';
import { CallbackResult, CallbackResultStatus } from '../../../../../../utils';


@Component({
  selector: 'app-gift-print-options',
  templateUrl: './gift-print-options.component.html',
  styleUrls: ['./gift-print-options.component.css']
})
export class GiftPrintOptionsComponent implements OnInit {

  public orgConfig: OrganizationConfig;
  public logoImageFileData: File = null;
  public bannerImageOptions: GiftPrintBannerImageOption[];

  public loadingOrgConfig = true;
  public loadingBannerImageOptions = true;
  public savingOrgConfig = false;

  public successMessage = '';
  public errorMessage = '';
  public errors: any = {};
  public modalResult: CallbackResult = null;

  public serverError: number = null;

  public readonly PrintPageSize = PrintPageSize;
  public readonly CallbackResultStatus = CallbackResultStatus;

  constructor(
    private orgConfigService: OrganizationConfigService,
    private giftService: GiftService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.loadOrganizationConfig();
    this.loadBannerImageOptions();
  }

  public attachLogoImageFile(event) {
    this.logoImageFileData = event.target.files[0] as File;
    if (this.logoImageFileData.type.match(/image\/*/) == null) {
      this.errors.gift_print_logo_image = 'Invalid image file.';
      this.logoImageFileData = null;
      return;
    }
    if (this.logoImageFileData.size > 2097152){
      this.errors.gift_print_logo_image = 'Image file is larger than 2 MB.';
      this.logoImageFileData = null;
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(this.logoImageFileData);
    reader.onload = () => {
      this.orgConfig.gift_print_logo_image = reader.result as string;
      this.saveOrganizationConfig();
    };
  }

  /**
   * Save the updated organization config to the API.
   */
  public saveOrganizationConfig() {
    this.savingOrgConfig = true;

    this.successMessage = '';
    this.errors = {};
    this.errorMessage = '';
    this.modalResult = null;

    this.orgConfigService
      .updateGiftPrintOptions(
        this.orgConfig,
        this.logoImageFileData,
      )
      .then(updatedOrgConf => {
        this.orgConfig = updatedOrgConf;
        this.successMessage = 'Gift print options updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to configure gift print options.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.savingOrgConfig = false; });
  }

  public handleAddBannerImageOption() {
    this.successMessage = '';
    this.errors = {};
    this.errorMessage = '';
    this.modalResult = null;

    const modalRef = this.modalService.open(
      ConfigureGiftPrintBannerImageOptionModalComponent
    );
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadBannerImageOptions();
    };
  }

  public handleEditBannerImageOption(opt: GiftPrintBannerImageOption) {
    this.successMessage = '';
    this.errors = {};
    this.errorMessage = '';
    this.modalResult = null;

    const modalRef = this.modalService.open(
      ConfigureGiftPrintBannerImageOptionModalComponent
    );
    modalRef.componentInstance.opt = opt;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadBannerImageOptions();
    };
  }

  public handleRemoveBannerImageOption(opt: GiftPrintBannerImageOption) {
    this.successMessage = '';
    this.errors = {};
    this.errorMessage = '';
    this.modalResult = null;

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Remove Banner Image Option';
    modalRef.componentInstance.messageText = 'Are you sure you want to remove this banner image option?';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingBannerImageOptions = true;

      this.giftService
        .removeGiftBannerImageOption(opt.url)
        .then(() => {
          this.successMessage = 'Banner image option "' + opt.name + '" removed successfully.';
          this.loadBannerImageOptions();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to remove this banner image option.';
          } else {
            this.serverError = reason.status;
          }
          this.loadingBannerImageOptions = false;
        });
    }
  }

  public openPreviewModal(opt: GiftPrintBannerImageOption) {
    const modalRef = this.modalService.open(
      GiftPdfPreviewModalComponent,
      {
        size: 'lg',
      }
    )
    modalRef.componentInstance.orgConfig = this.orgConfig;
    modalRef.componentInstance.bannerImageOption = opt;
  }

  /**
   * Load the organization config from the API.
   *
   * We do not use the local-stored copy, in order to avoid pop-in of changed
   * values.
   */
  private loadOrganizationConfig() {
    this.loadingOrgConfig = true;

    this.orgConfigService
      .loadOrganizationConfig()
      .then(() => {
        this.orgConfig = this.orgConfigService.getOrganizationConfig();
        this.loadingOrgConfig = false;
      });
  }

  private loadBannerImageOptions() {
    this.loadingBannerImageOptions = true;

    this.giftService
      .getGiftBannerImageOptions(1, 100)
      .then(result => {
        this.bannerImageOptions = result.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBannerImageOptions = false; });
  }

}
